/**
 * EvoHome brand / service contexts for the homeowner Home Control Centre.
 *
 * The homeowner keeps one identity and one Home Record across every EvoHome
 * service. This module only describes which service contexts exist, how they
 * are labelled for homeowners, and how much of each is genuinely live today.
 *
 * Availability is deliberately honest: a context that is not built yet is
 * marked "coming_soon" and must never be presented as operational.
 */

export type HomeBrandKey =
  | "my_home"
  | "improvements"
  | "maintenance"
  | "property"
  | "home_garden";

export type BrandAvailability = "live" | "limited" | "coming_soon";

export interface HomeBrand {
  key: HomeBrandKey;
  /** Short label used in the switcher. Homeowner language, never internal jargon. */
  label: string;
  /** One line explaining the context to a homeowner. */
  description: string;
  availability: BrandAvailability;
  /**
   * What the homeowner can genuinely do in this context today.
   * Empty for contexts that are not built yet.
   */
  capabilities: string[];
  /** Optional interest/enquiry action for contexts that are not live. */
  interestAction?: { label: string; href: string };
}

export const HOME_BRANDS: HomeBrand[] = [
  {
    key: "my_home",
    label: "My Home",
    description: "Everything about your home in one place.",
    availability: "live",
    capabilities: [
      "Home Record",
      "Property details",
      "Plans",
      "Projects",
      "Documents and guarantees",
      "Messages and support",
    ],
  },
  {
    key: "improvements",
    label: "Improvements",
    description: "Consultations, proposals and improvement projects.",
    availability: "live",
    capabilities: [
      "Planned improvements",
      "Enquiries and consultations",
      "Proposals",
      "Active projects",
      "Project documents and approvals",
      "Guarantees and aftercare",
    ],
  },
  {
    key: "maintenance",
    label: "Maintenance",
    description: "Repairs, upkeep and issues after your work is complete.",
    availability: "limited",
    capabilities: [
      "Report an issue on a completed project",
      "Track aftercare and repair requests",
      "See visit appointments",
      "Work history",
    ],
    interestAction: {
      label: "Ask about ongoing maintenance",
      href: "/customer/support",
    },
  },
  {
    key: "property",
    label: "Property",
    description: "Selling, buying, moving and preparing your property.",
    availability: "coming_soon",
    capabilities: [],
    interestAction: {
      label: "Register your interest",
      href: "/customer/support",
    },
  },
  {
    key: "home_garden",
    label: "Home & Garden",
    description: "Products and ideas connected to your home and plans.",
    availability: "coming_soon",
    capabilities: [],
    interestAction: {
      label: "Tell us what you are looking for",
      href: "/customer/support",
    },
  },
];

export const DEFAULT_BRAND: HomeBrandKey = "my_home";

export function getBrand(key: HomeBrandKey): HomeBrand {
  return HOME_BRANDS.find((b) => b.key === key) ?? HOME_BRANDS[0];
}

/** Accepts a URL parameter or stored value and returns a safe brand key. */
export function resolveBrandKey(value: string | null | undefined): HomeBrandKey {
  if (!value) return DEFAULT_BRAND;
  const match = HOME_BRANDS.find((b) => b.key === value);
  return match ? match.key : DEFAULT_BRAND;
}

/** True only when the homeowner can genuinely act inside this context. */
export function isBrandOperational(key: HomeBrandKey): boolean {
  return getBrand(key).availability !== "coming_soon";
}

const BRAND_STORAGE_PREFIX = "evohome.home.brand";
const PROPERTY_STORAGE_PREFIX = "evohome.home.property";

export function brandStorageKey(userId: string | null | undefined): string {
  return `${BRAND_STORAGE_PREFIX}.${userId || "anon"}`;
}

export function propertyStorageKey(userId: string | null | undefined): string {
  return `${PROPERTY_STORAGE_PREFIX}.${userId || "anon"}`;
}
